import React from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../../constants/Colors";
import StepWrapper from "./StepWrapper";
import SegmentedControl from "../SegmentedControl";
import {
  CreateFlatStore,
  Furnishing,
  MAX_ROOM_SIZE,
  MAX_FLAT_SIZE,
} from "../../hooks/useCreateFlatStore";

interface Props {
  store: CreateFlatStore;
}

const RoomDetailsStep: React.FC<Props> = ({ store }) => {
  const { t } = useTranslation();
  const {
    textNeutralDefault,
    textNeutralMuted,
    accentColor,
    containerNeutralLightMedium,
  } = useColors();

  const currency = store.getCurrency();

  const furnishingOptions: { key: Furnishing; label: string }[] = [
    { key: "unfurnished", label: t("unfurnished") || "Unfurnished" },
    {
      key: "partly_furnished",
      label: t("partly_furnished") || "Partly furnished",
    },
    { key: "furnished", label: t("furnished") || "Furnished" },
  ];

  const handleNumberInput = (
    value: string,
    setter: (val: string) => void,
    max: number,
  ) => {
    const cleaned = value.replace(/[^0-9]/g, "");
    if (cleaned === "" || parseInt(cleaned) <= max) {
      setter(cleaned);
    }
  };

  return (
    <StepWrapper
      title={t("flats_tab.add_flat.room_details_title") || "Room details"}
    >
      <div className="space-y-7">
        {/* Room size */}
        <div>
          <label
            className="block text-sm mb-2 font-medium"
            style={{ color: textNeutralMuted }}
          >
            {t("flats_tab.add_flat.room_size") || "Room size"}
          </label>
          <div className="flex items-center gap-3">
            <input
              type="text"
              inputMode="numeric"
              value={store.roomSize}
              onChange={(e) =>
                handleNumberInput(
                  e.target.value,
                  store.setRoomSize,
                  MAX_ROOM_SIZE,
                )
              }
              placeholder="0"
              className="cf-input-boxed flex-1"
              style={{ color: textNeutralDefault }}
            />
            <span
              className="text-sm font-medium flex-shrink-0"
              style={{ color: textNeutralMuted }}
            >
              m²
            </span>
          </div>
        </div>

        {/* Flat size toggle */}
        <div className="flex items-center justify-between">
          <span className="font-medium" style={{ color: textNeutralDefault }}>
            {t("flats_tab.add_flat.add_flat_size") ||
              "Add flat size (optional)"}
          </span>
          <button
            onClick={() => store.setAddingFlatSize(!store.addingFlatSize)}
            className="cf-toggle"
            style={{
              backgroundColor: store.addingFlatSize
                ? accentColor
                : containerNeutralLightMedium,
            }}
          >
            <span
              className="cf-toggle-knob"
              data-on={store.addingFlatSize ? "true" : "false"}
            />
          </button>
        </div>

        {/* Flat size (optional) */}
        {store.addingFlatSize && (
          <div>
            <label
              className="block text-sm mb-2 font-medium"
              style={{ color: textNeutralMuted }}
            >
              {t("flats_tab.add_flat.flat_size") || "Total flat size"}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                inputMode="numeric"
                value={store.flatSize}
                onChange={(e) =>
                  handleNumberInput(
                    e.target.value,
                    store.setFlatSize,
                    MAX_FLAT_SIZE,
                  )
                }
                placeholder="0"
                className="cf-input-boxed flex-1"
                style={{ color: textNeutralDefault }}
              />
              <span
                className="text-sm font-medium flex-shrink-0"
                style={{ color: textNeutralMuted }}
              >
                m²
              </span>
            </div>
          </div>
        )}

        {/* Furnishing */}
        <div>
          <label
            className="block text-sm mb-3 font-medium"
            style={{ color: textNeutralMuted }}
          >
            {t("flats_tab.add_flat.furnishing") || "Furnishing"}
          </label>
          <SegmentedControl
            options={furnishingOptions.map((opt) => ({
              value: opt.key,
              label: opt.label,
            }))}
            value={store.furnishing}
            onChange={(v) => store.setFurnishing(v)}
            fullWidth
          />
        </div>

        {/* Equipment fees (only for furnished/partly furnished) */}
        {store.furnishing !== "unfurnished" && (
          <div>
            <label
              className="block text-sm mb-2 font-medium"
              style={{ color: textNeutralMuted }}
            >
              {t("flats_tab.add_flat.equipment_fees") ||
                "Equipment/furnishing fee"}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                inputMode="numeric"
                value={store.equipmentFees}
                onChange={(e) =>
                  handleNumberInput(
                    e.target.value,
                    store.setEquipmentFees,
                    10000,
                  )
                }
                placeholder="0"
                className="cf-input-boxed flex-1"
                style={{ color: textNeutralDefault }}
              />
              <span
                className="text-sm font-medium flex-shrink-0"
                style={{ color: textNeutralMuted }}
              >
                {currency}
              </span>
            </div>
          </div>
        )}
      </div>
    </StepWrapper>
  );
};

export default RoomDetailsStep;
